import { Injectable } from '@angular/core';
import { BillingService, MerchantService, ProductPricing } from '@galaxy/billing';
import { AppPrices, AppPricingService, FieldMask, RequestedApp } from '@galaxy/marketplace-apps';
import { ResellerItem } from '@vendasta/store';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { AppConfigService } from '../../app-config.service';
import { ActivationPricingServiceInterface } from '../../common/app-selection/paginated-app-activation-table/activation-pricing-service-interface';
import { AccountService } from '../../core/account.service';
import { BillingApiService } from '../../core/billing/billing-api.service';
import { PricingService } from '../../core/billing/pricing.service';
import { ContractDetailsService } from '../../core/contract-details.service';
import { HidePricingGuard } from '../../core/guards/hide-pricing-guard.service';
import { resellerArrayToMap } from '../../core/reseller';
import { ResellerItemService } from '../../core/reseller/reseller-item.service';
import { BillingSettingsService } from '../../shared-billing/settings/billing-settings.service';
import { UniqueIdToItemIdAndQuantityMap } from '../order-form-page/interface';
import { ActivationParamService } from './activation-param.service';
import { filterResellerItemQuantity } from './utils';

/* responsible for providing all pricing hooks for activation */
@Injectable()
export class ActivationPricingService implements ActivationPricingServiceInterface {
  // Map of sku to ProductPricing. Contains pricing for all activatable items
  readonly productPricingMap$: Observable<Map<string, ProductPricing>>;
  readonly retailProductPricingMap$: Observable<Map<string, ProductPricing>>;

  readonly appPriceMap$: Observable<Map<string, AppPrices>>;
  readonly partnerCurrency$: Observable<string>;
  readonly marketCurrency$: Observable<string>;

  readonly hidePricing$: Observable<boolean>;
  readonly userCanEditBillingInfo$: Observable<boolean>;
  readonly billingInfoConfigured$: Observable<boolean>;

  private readonly activatableResellerItems$: Observable<ResellerItem[]>;
  private readonly partnerId$: Observable<string>;
  private readonly accountGroupId$: Observable<string>;
  private readonly resellerItemMap$: Observable<Map<string, ResellerItem>>;
  private readonly resellerItemQuantityMap$: Observable<UniqueIdToItemIdAndQuantityMap>;
  private marketId$: Observable<string>;

  constructor(
    private resellerItemService: ResellerItemService,
    private paramService: ActivationParamService,
    private pricingService: PricingService,
    private billingApiService: BillingApiService,
    private accountService: AccountService,
    private app: AppConfigService,
    private hidePricingGuard: HidePricingGuard,
    private contractDetailsService: ContractDetailsService,
    private billingSettings: BillingSettingsService,
    private appPricingService: AppPricingService,
    private billingService: BillingService,
    private merchantService: MerchantService,
  ) {
    this.partnerId$ = this.paramService.partnerId$;
    this.accountGroupId$ = this.paramService.accountGroupId$;
    this.activatableResellerItems$ = this.resellerItemService.getActivatablePartnerDataWithoutSuspended();
    this.marketId$ = this.paramService.marketId$;

    this.resellerItemMap$ = this.activatableResellerItems$.pipe(
      map((resellerItems) => resellerArrayToMap(resellerItems)),
    );

    const appStatuses$ = this.accountGroupId$.pipe(
      switchMap((accountId) => this.accountService.getAppStatusesForBusiness(accountId)),
      shareReplay(1),
    );

    const activations$ = this.accountService.activations$;

    this.resellerItemQuantityMap$ = combineLatest([
      this.paramService.resellerItemQuantityMapFromUrl$,
      appStatuses$,
      this.resellerItemMap$,
      activations$.pipe(startWith([])),
    ]).pipe(
      map(([resellerQuantityMap, appStatuses, resellerItemMap, activations]) => {
        return filterResellerItemQuantity(resellerItemMap, resellerQuantityMap, appStatuses, activations);
      }),
    );

    this.hidePricing$ = this.hidePricingGuard.canActivate().pipe(shareReplay(1));
    this.userCanEditBillingInfo$ = app.config$.pipe(map((config) => config.userCanAccessBilling));
    this.billingInfoConfigured$ = combineLatest([
      this.billingSettings.isMerchantConfigured,
      this.billingSettings.isPaymentDetailsConfigured,
    ]).pipe(
      map(([merchantConfigured, paymentConfigured]) => merchantConfigured && paymentConfigured),
      shareReplay(1),
    );

    this.retailProductPricingMap$ = combineLatest([this.partnerId$, this.activatableResellerItems$]).pipe(
      switchMap(([partnerId, resellerItems]) => this.getMultiRetailProductPricing(partnerId, resellerItems)),
      map((skuPricingObject) => {
        return this.getProductPricingMapping(skuPricingObject);
      }),
      shareReplay(1),
    );

    this.productPricingMap$ = combineLatest([this.partnerId$, this.activatableResellerItems$]).pipe(
      switchMap(([partnerId, resellerItems]) => this.getMultiProductPricing(partnerId, resellerItems)),
      map((skuPricingObject) => {
        return this.getProductPricingMapping(skuPricingObject);
      }),
      shareReplay(1),
    );

    this.appPriceMap$ = combineLatest([this.partnerId$, this.activatableResellerItems$]).pipe(
      filter(([_, resellerItems]) => resellerItems.length > 0),
      switchMap(([partnerId, resellerItems]) => this.getMultiPricing(partnerId, resellerItems)),
      map((pricingObject) => {
        return this.getAppPriceMapping(pricingObject);
      }),
      shareReplay(1),
    );

    this.partnerCurrency$ = this.partnerId$?.pipe(
      switchMap((partnerId) =>
        this.billingService.getMultiContract([partnerId]).pipe(map((resp) => resp[partnerId]?.currency ?? 'USD')),
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.marketCurrency$ = combineLatest([this.paramService.partnerId$, this.paramService.marketId$]).pipe(
      switchMap(([partnerId, marketId]) => this.merchantService.getMultiRetailConfigurations(partnerId, [marketId])),
      map((retailConfigs) => {
        const [retailConfig] = retailConfigs.values();
        return retailConfig?.currencyCode;
      }),
      catchError(() => {
        // If there are any errors getting retail configurations, return an empty currency code
        return of('');
      }),
    );
  }

  getMultiRetailProductPricing(
    partnerId: string,
    resellerItems: ResellerItem[],
  ): Observable<{ [sku: string]: ProductPricing }> {
    if (resellerItems) {
      const skus = resellerItems
        .map((resellerItem) => {
          return resellerItem.billingId;
        })
        .filter(Boolean);
      if (!skus || skus.length === 0) {
        return of({});
      }
      return this.marketId$.pipe(
        switchMap((marketId) => this.pricingService.getMultiRetailPricing(partnerId, skus, null, marketId)),
      );
    } else {
      return of();
    }
  }

  getMultiProductPricing(
    partnerId: string,
    resellerItems: ResellerItem[],
  ): Observable<{ [sku: string]: ProductPricing }> {
    if (resellerItems) {
      const skus = resellerItems
        .map((resellerItem) => {
          return resellerItem.billingId;
        })
        .filter(Boolean);
      if (!skus || skus.length === 0) {
        return of({});
      }
      return this.pricingService.getMultiProductPricing(partnerId, skus);
    } else {
      return of();
    }
  }

  // Fetch pricing information with instance prices, since it's an activation flow
  getMultiPricing(partnerId: string, resellerItems: ResellerItem[]): Observable<AppPrices[]> {
    if (resellerItems) {
      const appAndAddonIds = resellerItems.map((item) => (item.addonId ? item.addonId : item.appId));
      const requestedApps = appAndAddonIds.map((item) => new RequestedApp({ appId: item, quantity: 1 }));

      const appPrices = this.marketId$.pipe(
        switchMap((marketId) =>
          this.appPricingService.getMultiPricing(
            appAndAddonIds,
            new FieldMask({ paths: ['wholesale', 'retail'] }),
            partnerId,
            marketId,
            new FieldMask({ paths: ['instance_price'] }),
            requestedApps,
          ),
        ),
      );

      return appPrices;
    }
  }

  getProductPricingMapping(skuPricingObject: { [sku: string]: ProductPricing }): Map<string, ProductPricing> {
    const m = new Map<string, ProductPricing>();
    Object.keys(skuPricingObject).forEach((key) => {
      m.set(key, skuPricingObject[key]);
    });
    return m;
  }

  getAppPriceMapping(appPrices: AppPrices[]): Map<string, AppPrices> {
    const appPricesMap = new Map<string, AppPrices>();

    appPrices.map((p) => {
      appPricesMap.set(p.appId, p);
    });

    return appPricesMap;
  }
}
